import { useState, useEffect } from 'react';
import { useAuthStore } from '@/store';
import { api } from '@/store';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Key, Plus, Trash2, Copy, RefreshCw, Shield, Clock, Globe, FileText, ChevronRight, Eye, EyeOff, AlertTriangle, CheckCircle2, Loader2, Activity, Code, Puzzle, Download } from 'lucide-react';

const ALL_PERMISSIONS = [
  { key: 'customers.read', label: 'Consultar clientes', group: 'Clientes' },
  { key: 'customers.create', label: 'Criar clientes', group: 'Clientes' },
  { key: 'customers.update', label: 'Editar clientes', group: 'Clientes' },
  { key: 'customers.delete', label: 'Excluir clientes', group: 'Clientes' },
  { key: 'plans.read', label: 'Consultar pacotes', group: 'Pacotes' },
  { key: 'plans.create', label: 'Criar pacotes', group: 'Pacotes' },
  { key: 'plans.update', label: 'Editar pacotes', group: 'Pacotes' },
  { key: 'plans.delete', label: 'Excluir pacotes', group: 'Pacotes' },
  { key: 'subscriptions.renew', label: 'Renovar assinaturas', group: 'Assinaturas' },
  { key: 'subscriptions.suspend', label: 'Suspender clientes', group: 'Assinaturas' },
  { key: 'subscriptions.reactivate', label: 'Reativar clientes', group: 'Assinaturas' },
  { key: 'payments.create', label: 'Gerar pagamentos (PIX)', group: 'Pagamentos' },
  { key: 'payments.read', label: 'Consultar pagamentos', group: 'Pagamentos' },
];


const CODE_SAMPLES = {
  python: `import hmac, hashlib, time, json, requests

API_KEY = "nzk_..."
SECRET_KEY = "nzs_..."
BASE = "https://nexuzap.com"

def nexuzap(method, path, body=None, params=None, extra_headers=None):
    raw = json.dumps(body, separators=(",", ":")) if body is not None else ""
    ts = str(int(time.time()))
    key = hashlib.sha256(SECRET_KEY.encode()).hexdigest()          # 1) SHA256 da secret
    msg = method + path + ts + raw                                  # 2) sem query string
    sig = hmac.new(key.encode(), msg.encode(), hashlib.sha256).hexdigest()
    headers = {"X-API-Key": API_KEY, "X-Timestamp": ts, "X-Signature": sig, **(extra_headers or {})}
    if raw:
        headers["Content-Type"] = "application/json"
    r = requests.request(method, BASE + path, params=params, data=raw or None, headers=headers)
    r.raise_for_status()
    return r.json()

print(nexuzap("GET", "/api/v1/customers", params={"page": 1, "status": "expiring"}))
print(nexuzap("POST", "/api/v1/customers/ID_DO_CLIENTE/renew", {}))

# --- Pagamento: gerar PIX e mostrar ao cliente ---
cliente = nexuzap("GET", "/api/v1/customers/by-external/cli-8841")   # seu id -> cliente NexuZap
pay = nexuzap("POST", f"/api/v1/customers/{cliente['id']}/payments", {
    "callback_url": "https://seusite.com/webhooks/nexuzap",   # opcional (ou webhook fixo da chave)
    "external_reference": "pedido-123",                      # opcional
}, extra_headers={"Idempotency-Key": "pedido-123"})           # clicou 2x? volta a mesma cobrança
copia_e_cola = pay["pix"]["copy_paste"]
img_html = f'<img src="{pay["pix"]["qr_code_data_uri"]}">'      # ou pay["pix"]["qr_code_base64"]

# sem webhook? consulte até aprovar:
status = nexuzap("GET", f"/api/v1/payments/{pay['id']}")
if status["status"] == "approved":
    print("renovado até", status["renewal"]["new_expiration"])

# --- Receber o webhook (Flask) ---
# @app.post("/webhooks/nexuzap")
# def hook():
#     raw = request.get_data(as_text=True)
#     ts = request.headers["X-NexuZap-Timestamp"]
#     key = hashlib.sha256(SECRET_KEY.encode()).hexdigest()
#     esperado = hmac.new(key.encode(), (ts + raw).encode(), hashlib.sha256).hexdigest()
#     if not hmac.compare_digest(esperado, request.headers["X-NexuZap-Signature"]):
#         return "assinatura inválida", 401
#     evento = json.loads(raw)   # evento["event"] == "payment.approved"
#     return "ok"`,
  node: `const crypto = require("crypto");

const API_KEY = "nzk_...";
const SECRET_KEY = "nzs_...";
const BASE = "https://nexuzap.com";

async function nexuzap(method, path, body, query = "") {
  const raw = body !== undefined ? JSON.stringify(body) : "";
  const ts = Math.floor(Date.now() / 1000).toString();
  const key = crypto.createHash("sha256").update(SECRET_KEY).digest("hex");   // 1) SHA256 da secret
  const sig = crypto.createHmac("sha256", key).update(method + path + ts + raw).digest("hex"); // 2) sem query
  const res = await fetch(BASE + path + query, {
    method,
    headers: { "X-API-Key": API_KEY, "X-Timestamp": ts, "X-Signature": sig, ...(raw && { "Content-Type": "application/json" }) },
    body: raw || undefined,
  });
  if (!res.ok) throw new Error(res.status + " " + (await res.text()));
  return res.json();
}

nexuzap("GET", "/api/v1/customers", undefined, "?page=1").then(console.log);
nexuzap("POST", "/api/v1/customers", { name: "Ana", phone: "5521999999999", plan_id: "ID_DO_PLANO" }).then(console.log);

// --- Pagamento: gerar PIX ---
(async () => {
  const pay = await nexuzap("POST", "/api/v1/customers/ID_DO_CLIENTE/payments", {
    callback_url: "https://seusite.com/webhooks/nexuzap", external_reference: "pedido-123",
  });
  console.log(pay.pix.copy_paste);       // PIX copia e cola
  console.log(pay.pix.qr_code_data_uri); // use direto em <img src="...">
})();

// --- Receber o webhook (Express, com o corpo bruto) ---
// app.post("/webhooks/nexuzap", express.raw({ type: "application/json" }), (req, res) => {
//   const raw = req.body.toString("utf8");
//   const key = crypto.createHash("sha256").update(SECRET_KEY).digest("hex");
//   const esperado = crypto.createHmac("sha256", key).update(req.get("X-NexuZap-Timestamp") + raw).digest("hex");
//   if (esperado !== req.get("X-NexuZap-Signature")) return res.status(401).end();
//   const evento = JSON.parse(raw); // evento.event === "payment.approved"
//   res.sendStatus(200);
// });`,
  php: `<?php
$API_KEY = "nzk_...";
$SECRET_KEY = "nzs_...";
$BASE = "https://nexuzap.com";

function nexuzap($method, $path, $body = null, $query = "") {
    global $API_KEY, $SECRET_KEY, $BASE;
    $raw = $body !== null ? json_encode($body, JSON_UNESCAPED_UNICODE | JSON_UNESCAPED_SLASHES) : "";
    $ts = (string) time();
    $key = hash("sha256", $SECRET_KEY);                                  // 1) SHA256 da secret
    $sig = hash_hmac("sha256", $method . $path . $ts . $raw, $key);      // 2) sem query string
    $headers = ["X-API-Key: $API_KEY", "X-Timestamp: $ts", "X-Signature: $sig"];
    if ($raw !== "") $headers[] = "Content-Type: application/json";
    $ch = curl_init($BASE . $path . $query);
    curl_setopt_array($ch, [
        CURLOPT_CUSTOMREQUEST => $method, CURLOPT_HTTPHEADER => $headers,
        CURLOPT_RETURNTRANSFER => true, CURLOPT_POSTFIELDS => $raw !== "" ? $raw : null,
    ]);
    $out = curl_exec($ch);
    curl_close($ch);
    return json_decode($out, true);
}

print_r(nexuzap("GET", "/api/v1/plans"));
print_r(nexuzap("POST", "/api/v1/customers/ID_DO_CLIENTE/renew", ["months" => 1]));

// --- Pagamento: gerar PIX ---
$pay = nexuzap("POST", "/api/v1/customers/ID_DO_CLIENTE/payments", [
    "callback_url" => "https://seusite.com/webhooks/nexuzap", "external_reference" => "pedido-123",
]);
echo $pay["pix"]["copy_paste"];                                  // PIX copia e cola
echo '<img src="' . $pay["pix"]["qr_code_data_uri"] . '">';     // QR Code

// --- Receber o webhook ---
// $raw = file_get_contents("php://input");
// $key = hash("sha256", $SECRET_KEY);
// $esperado = hash_hmac("sha256", $_SERVER["HTTP_X_NEXUZAP_TIMESTAMP"] . $raw, $key);
// if (!hash_equals($esperado, $_SERVER["HTTP_X_NEXUZAP_SIGNATURE"])) { http_response_code(401); exit; }
// $evento = json_decode($raw, true); // $evento["event"] === "payment.approved"`,
};

export default function ApiIntegrationPage() {
  const { user } = useAuthStore();
  const [tab, setTab] = useState('keys');
  const [codeLang, setCodeLang] = useState('python');
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [editKey, setEditKey] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsKey, setLogsKey] = useState(null);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [docsData, setDocsData] = useState(null);
  
  // Create form
  const [formName, setFormName] = useState('');
  const [formPerms, setFormPerms] = useState(ALL_PERMISSIONS.map(p => p.key));
  const [formRate, setFormRate] = useState(120);
  const [formIps, setFormIps] = useState('');
  const [formWebhook, setFormWebhook] = useState('');
  const [creating, setCreating] = useState(false);

  const loadKeys = async () => {
    try { const r = await api.get('/api-keys'); setKeys(r.data); } catch {} finally { setLoading(false); }
  };
  
  useEffect(() => { loadKeys(); }, []);

  const handleCreate = async () => {
    if (!formName.trim()) return toast.error('Nome é obrigatório');
    setCreating(true);
    try {
      const ips = formIps.split('\n').map(s => s.trim()).filter(Boolean);
      const r = await api.post('/api-keys', { name: formName, permissions: formPerms, rate_limit: formRate, ip_whitelist: ips, webhook_url: formWebhook.trim() || null });
      setNewKeyResult(r.data);
      setCreateOpen(false);
      setFormName(''); setFormPerms(ALL_PERMISSIONS.map(p => p.key)); setFormRate(120); setFormIps(''); setFormWebhook('');
      loadKeys();
      toast.success('Chave criada!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Revogar esta chave? Integrações que usam ela pararão de funcionar.')) return;
    try { await api.delete(`/api-keys/${id}`); loadKeys(); toast.success('Chave revogada'); } catch { toast.error('Erro'); }
  };

  const handleRotate = async (id) => {
    if (!confirm('Rotacionar secret key? A chave anterior será invalidada.')) return;
    try {
      const r = await api.post(`/api-keys/${id}/rotate`);
      setNewKeyResult({ ...keys.find(k => k.id === id), secret_key: r.data.secret_key, _rotated: true });
      toast.success('Secret rotacionada');
    } catch { toast.error('Erro'); }
  };

  const handleUpdate = async () => {
    if (!editKey) return;
    try {
      const ips = (editKey._ips || '').split('\n').map(s => s.trim()).filter(Boolean);
      await api.put(`/api-keys/${editKey.id}`, {
        name: editKey.name, permissions: editKey.permissions,
        rate_limit: editKey.rate_limit, ip_whitelist: ips, active: editKey.active,
        webhook_url: (editKey.webhook_url || '').trim() || null
      });
      setEditKey(null); loadKeys(); toast.success('Atualizada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const loadLogs = async (keyId, page = 1) => {
    setLogsKey(keyId); setLogsPage(page);
    try { const r = await api.get(`/api-keys/${keyId}/logs?page=${page}&limit=30`); setLogs(r.data.logs); setLogsTotal(r.data.total); } catch {}
  };

  const loadDocs = async () => {
    try { const r = await api.get('/api-keys/docs-data'); setDocsData(r.data); } catch {}
  };

  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success('Copiado!'); };

  const tabs = [
    { id: 'keys', label: 'Chaves', icon: Key },
    { id: 'extension', label: 'Extensão WhatsApp', icon: Puzzle },
    { id: 'docs', label: 'Documentação', icon: FileText },
  ];

  return (
    <div className="space-y-6" data-testid="api-integration-page">
      <div>
        <h1 className="text-2xl font-bold">API de Integração</h1>
        <p className="text-sm text-muted-foreground">Gerencie suas chaves de API e consulte a documentação</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-lg bg-muted/30 w-fit">
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'docs' && !docsData) loadDocs(); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {/* EXTENSION TAB */}
      {tab === 'extension' && (
        <div className="space-y-5 max-w-3xl">
          <div className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/[0.07] to-transparent p-6">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-14 h-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
                <Puzzle className="w-7 h-7" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <h2 className="text-lg font-bold">NexuZap CRM para WhatsApp Web</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Gerencie seus clientes sem sair do WhatsApp. Ao abrir a conversa de um cliente, veja o vencimento, plano e valor — e renove, gere PIX ou cadastre em segundos.
                </p>
              </div>
              <a href="/nexuzap-crm-extension.zip" download
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-xl px-4 py-2.5 font-semibold text-sm hover:opacity-90">
                <Download className="w-4 h-4" /> Baixar extensão
              </a>
            </div>
          </div>

          {/* Recursos */}
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              ['🔍', 'Identifica o cliente', 'Abre o chat e mostra na hora se o número é seu cliente, com vencimento e dias restantes.'],
              ['🔄', 'Renova em 1 clique', 'Renova o cliente direto do WhatsApp — atualiza no seu painel automaticamente.'],
              ['💳', 'Gera PIX na hora', 'Cria o PIX de renovação e envia no chat, com QR Code e copia-e-cola.'],
              ['➕', 'Cadastra rápido', 'Número novo? Cadastre o cliente com o telefone já preenchido.'],
            ].map(([ic, t, d]) => (
              <div key={t} className="rounded-xl border border-border/60 p-4">
                <p className="font-semibold text-sm">{ic} {t}</p>
                <p className="text-xs text-muted-foreground mt-1">{d}</p>
              </div>
            ))}
          </div>

          {/* Passo a passo */}
          <div className="rounded-2xl border border-border/60 p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Como instalar (2 minutos)</h3>
            <ol className="space-y-4">
              {[
                ['Baixe e descompacte', 'Clique em "Baixar extensão" acima e extraia o arquivo .zip numa pasta no seu computador.'],
                ['Abra as extensões do Chrome', 'Digite chrome://extensions na barra de endereço e ative o "Modo do desenvolvedor" (canto superior direito).'],
                ['Carregue a extensão', 'Clique em "Carregar sem compactação" e escolha a pasta que você descompactou.'],
                ['Conecte sua conta', 'Clique no ícone da extensão (quebra-cabeça na barra do Chrome), cole sua API Key e Secret (crie na aba "Chaves") e salve.'],
                ['Use no WhatsApp Web', 'Abra o web.whatsapp.com, entre na conversa de um cliente e clique no botão 💼 no canto da tela.'],
              ].map(([t, d], i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-primary/15 text-primary font-bold flex items-center justify-center shrink-0 text-sm">{i + 1}</span>
                  <div><p className="font-medium text-sm">{t}</p><p className="text-xs text-muted-foreground mt-0.5">{d}</p></div>
                </li>
              ))}
            </ol>
            <div className="mt-4 flex items-start gap-2 text-xs text-amber-500 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              Crie primeiro uma chave na aba <b>Chaves</b> com as permissões de clientes, pacotes e pagamentos. É ela que a extensão usa.
            </div>
          </div>
        </div>
      )}

      {/* KEYS TAB */}
      {tab === 'keys' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{keys.length} chave(s) criada(s)</p>
            <Button onClick={() => setCreateOpen(true)} size="sm"><Plus className="w-4 h-4 mr-1" />Nova Chave</Button>
          </div>

          {loading ? <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div> : keys.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <Key className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Nenhuma chave de API criada</p>
              <Button onClick={() => setCreateOpen(true)} className="mt-4" size="sm"><Plus className="w-4 h-4 mr-1" />Criar Primeira Chave</Button>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {keys.map(k => (
                <Card key={k.id} className={!k.active ? 'opacity-50' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-sm">{k.name}</h3>
                          <Badge variant={k.active ? 'default' : 'secondary'} className="text-[10px]">{k.active ? 'Ativa' : 'Inativa'}</Badge>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono truncate max-w-[250px]">{k.api_key}</code>
                          <button onClick={() => copyText(k.api_key)} className="text-muted-foreground hover:text-foreground"><Copy className="w-3 h-3" /></button>
                        </div>
                        <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground">
                          <span className="flex items-center gap-1"><Shield className="w-3 h-3" />{k.permissions?.length || 0} permissões</span>
                          <span className="flex items-center gap-1"><Activity className="w-3 h-3" />{k.rate_limit || 120}/min</span>
                          <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{k.ip_whitelist?.length > 0 ? `${k.ip_whitelist.length} IP(s)` : 'Todos'}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{k.requests_count || 0} reqs</span>
                          {k.last_used_at && <span>Último uso: {new Date(k.last_used_at).toLocaleDateString('pt-BR')}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => loadLogs(k.id)} title="Logs"><FileText className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditKey({ ...k, _ips: (k.ip_whitelist || []).join('\n') }); }} title="Editar"><Code className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRotate(k.id)} title="Rotacionar Secret"><RefreshCw className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(k.id)} title="Revogar"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Logs inline */}
          {logsKey && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-blue-500" />Logs de Uso ({logsTotal})</CardTitle>
                  <button onClick={() => setLogsKey(null)} className="text-xs text-muted-foreground hover:text-foreground">Fechar</button>
                </div>
              </CardHeader>
              <CardContent>
                {logs.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">Nenhum log</p> : (
                  <div className="max-h-80 overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 sticky top-0"><tr>
                        <th className="p-2 text-left">Data</th><th className="p-2 text-left">Método</th><th className="p-2 text-left">Endpoint</th>
                        <th className="p-2">Status</th><th className="p-2">IP</th><th className="p-2">Tempo</th>
                      </tr></thead>
                      <tbody>
                        {logs.map(l => (
                          <tr key={l.id} className="border-t border-border/50">
                            <td className="p-2">{new Date(l.timestamp).toLocaleString('pt-BR')}</td>
                            <td className="p-2"><Badge variant="outline" className="text-[10px]">{l.method}</Badge></td>
                            <td className="p-2 font-mono truncate max-w-[200px]">{l.endpoint}</td>
                            <td className="p-2 text-center"><Badge className={`text-[10px] ${l.status_code < 400 ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>{l.status_code}</Badge></td>
                            <td className="p-2 font-mono">{l.ip}</td>
                            <td className="p-2 text-right">{l.response_time_ms}ms</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* DOCS TAB */}
      {tab === 'docs' && docsData && (
        <div className="space-y-4">
          {/* Auth */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Shield className="w-4 h-4 text-green-500" />Autenticação HMAC SHA-256</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">{docsData.auth.description}. O timestamp deve estar dentro de 5 minutos.</p>
              <div className="bg-muted/30 rounded-lg p-3 space-y-1">
                {Object.entries(docsData.auth.headers).map(([k, v]) => (
                  <div key={k} className="flex gap-2 text-xs"><code className="text-primary font-mono font-bold">{k}:</code><span className="text-muted-foreground">{v}</span></div>
                ))}
              </div>
              {docsData.auth.steps && (
                <ol className="list-decimal pl-5 space-y-1 text-xs text-muted-foreground">
                  {docsData.auth.steps.map((st, i) => <li key={i}>{st}</li>)}
                </ol>
              )}
              <div className="flex gap-1 flex-wrap">
                {['python', 'node', 'php'].map((lang) => (
                  <Button key={lang} size="sm" variant={codeLang === lang ? 'default' : 'outline'} className="h-7 text-xs" onClick={() => setCodeLang(lang)}>
                    {{ python: 'Python', node: 'Node.js', php: 'PHP' }[lang]}
                  </Button>
                ))}
              </div>
              <div className="bg-muted/30 rounded-lg p-3 relative">
                <Button size="sm" variant="ghost" className="absolute top-2 right-2 h-7 text-xs" onClick={() => { navigator.clipboard.writeText(CODE_SAMPLES[codeLang]); toast.success('Exemplo copiado'); }}>
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                </Button>
                <pre className="text-[11px] font-mono text-foreground overflow-x-auto whitespace-pre pr-20">{CODE_SAMPLES[codeLang]}</pre>
              </div>
            </CardContent>
          </Card>

          {/* Endpoints */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Code className="w-4 h-4 text-blue-500" />Endpoints</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {docsData.endpoints.map((ep, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/20 border border-border/50">
                    <Badge className={`text-[10px] font-mono shrink-0 ${ep.method === 'GET' ? 'bg-blue-500/20 text-blue-500' : ep.method === 'POST' ? 'bg-green-500/20 text-green-500' : ep.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-500' : 'bg-red-500/20 text-red-500'}`}>{ep.method}</Badge>
                    <div className="flex-1 min-w-0">
                      <code className="text-xs font-mono font-bold text-foreground">{ep.path}</code>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{ep.desc}</p>
                      {ep.permission && <Badge variant="outline" className="text-[10px] mt-1">{ep.permission}</Badge>}
                      {ep.body && <pre className="text-[10px] font-mono text-muted-foreground mt-1 bg-muted/30 rounded p-1.5 overflow-x-auto">{ep.body}</pre>}
                      {ep.params && <p className="text-[10px] text-muted-foreground mt-1">Params: <code>{ep.params}</code></p>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Objeto cliente */}
          {docsData.customer_object && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-amber-500" />Objeto cliente</CardTitle></CardHeader>
              <CardContent>
                <div className="divide-y divide-border/50">
                  {docsData.customer_object.map((f) => (
                    <div key={f.field} className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-1 py-2 text-xs">
                      <code className="font-mono font-bold text-primary">{f.field}</code>
                      <span className="text-muted-foreground">{f.desc}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Webhooks */}
          {docsData.webhooks && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-purple-500" />Webhook de pagamento</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">{docsData.webhooks.description}</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {docsData.webhooks.events.map((ev) => (
                    <div key={ev.event} className="p-2 rounded-lg bg-muted/20 border border-border/50">
                      <code className="text-xs font-mono font-bold text-primary">{ev.event}</code>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{ev.desc}</p>
                    </div>
                  ))}
                </div>
                <div className="bg-muted/30 rounded-lg p-3 space-y-1">
                  {Object.entries(docsData.webhooks.headers).map(([k, v]) => (
                    <div key={k} className="flex gap-2 text-xs"><code className="text-primary font-mono font-bold shrink-0">{k}:</code><span className="text-muted-foreground">{v}</span></div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">{docsData.webhooks.verify}</p>
                {docsData.webhooks.panel_retry && (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                    <p className="text-[11px] font-bold text-amber-500 mb-1">Renovação no painel com falha</p>
                    <p className="text-xs text-muted-foreground">{docsData.webhooks.panel_retry}</p>
                  </div>
                )}
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Exemplo de corpo (payment.approved):</p>
                  <pre className="text-[11px] font-mono text-foreground overflow-x-auto whitespace-pre">{(() => { try { return JSON.stringify(JSON.parse(docsData.webhooks.example), null, 2); } catch { return docsData.webhooks.example; } })()}</pre>
                </div>
              </CardContent>
            </Card>
          )}

          {/* HTTP Codes */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Códigos HTTP</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(docsData.responses).map(([code, desc]) => (
                  <div key={code} className="flex items-center gap-2 p-2 rounded-lg bg-muted/20 text-xs">
                    <Badge className={`text-[10px] ${parseInt(code) < 400 ? 'bg-green-500/20 text-green-500' : parseInt(code) < 500 ? 'bg-yellow-500/20 text-yellow-500' : 'bg-red-500/20 text-red-500'}`}>{code}</Badge>
                    <span className="text-muted-foreground">{desc}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Key Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nova Chave de API</DialogTitle><DialogDescription>A secret key aparece só uma vez depois de criar. Guarde em local seguro.</DialogDescription></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label className="text-xs">Nome da Integração</Label><Input value={formName} onChange={e => setFormName(e.target.value)} placeholder="Ex: Meu ERP" className="h-9 mt-1" /></div>
            <div><Label className="text-xs">Rate Limit (req/min)</Label><Input type="number" min={1} max={10000} value={formRate} onChange={e => setFormRate(parseInt(e.target.value) || 120)} className="h-9 mt-1" /></div>
            <div><Label className="text-xs">IPs Permitidos (1 por linha, vazio = todos)</Label><textarea value={formIps} onChange={e => setFormIps(e.target.value)} rows={2} placeholder="Ex: 192.168.1.1" className="w-full text-xs p-2 rounded-md border bg-background mt-1 resize-none" /></div>
            <div>
              <Label className="text-xs">Webhook fixo (opcional)</Label>
              <Input value={formWebhook} onChange={e => setFormWebhook(e.target.value)} placeholder="https://seusite.com/webhooks/nexuzap" className="h-9 mt-1" />
              <p className="text-[11px] text-muted-foreground mt-1">Recebe os avisos de pagamento quando a cobrança for gerada sem callback_url.</p>
            </div>
            <div>
              <Label className="text-xs mb-2 block">Permissões</Label>
              <div className="space-y-3">
                {['Clientes', 'Pacotes', 'Assinaturas', 'Pagamentos'].map(group => (
                  <div key={group}>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">{group}</p>
                    <div className="grid grid-cols-2 gap-1">
                      {ALL_PERMISSIONS.filter(p => p.group === group).map(p => (
                        <label key={p.key} className="flex items-center gap-2 text-xs cursor-pointer p-1.5 rounded hover:bg-muted/30">
                          <Checkbox checked={formPerms.includes(p.key)} onCheckedChange={c => c ? setFormPerms([...formPerms, p.key]) : setFormPerms(formPerms.filter(x => x !== p.key))} />
                          {p.label}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={handleCreate} disabled={creating} className="w-full">{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Criar Chave'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Key Result Dialog */}
      <Dialog open={!!newKeyResult} onOpenChange={() => setNewKeyResult(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-green-500" />{newKeyResult?._rotated ? 'Secret Rotacionada' : 'Chave Criada!'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <p className="text-xs text-yellow-500 font-bold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Salve a Secret Key agora! Ela não será exibida novamente.</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">API Key (pública)</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 text-xs bg-muted px-3 py-2 rounded font-mono break-all">{newKeyResult?.api_key}</code>
                <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={() => copyText(newKeyResult?.api_key)}><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-yellow-500 font-bold">Secret Key (SALVE AGORA!)</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 text-xs bg-yellow-500/10 border border-yellow-500/30 px-3 py-2 rounded font-mono break-all text-yellow-500">{newKeyResult?.secret_key}</code>
                <Button variant="outline" size="icon" className="h-8 w-8 shrink-0 border-yellow-500/30" onClick={() => copyText(newKeyResult?.secret_key)}><Copy className="w-3.5 h-3.5 text-yellow-500" /></Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Key Dialog */}
      <Dialog open={!!editKey} onOpenChange={() => setEditKey(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar Chave</DialogTitle></DialogHeader>
          {editKey && (
            <div className="space-y-4 pt-2">
              <div><Label className="text-xs">Nome</Label><Input value={editKey.name} onChange={e => setEditKey({...editKey, name: e.target.value})} className="h-9 mt-1" /></div>
              <div className="flex items-center gap-3">
                <Label className="text-xs">Ativa</Label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={editKey.active} onChange={e => setEditKey({...editKey, active: e.target.checked})} className="sr-only peer" />
                  <div className="w-10 h-5 bg-muted rounded-full peer peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5"></div>
                </label>
              </div>
              <div><Label className="text-xs">Rate Limit</Label><Input type="number" value={editKey.rate_limit} onChange={e => setEditKey({...editKey, rate_limit: parseInt(e.target.value) || 120})} className="h-9 mt-1" /></div>
              <div><Label className="text-xs">IPs (1 por linha)</Label><textarea value={editKey._ips} onChange={e => setEditKey({...editKey, _ips: e.target.value})} rows={2} className="w-full text-xs p-2 rounded-md border bg-background mt-1 resize-none" /></div>
              <div>
                <Label className="text-xs">Webhook fixo (opcional)</Label>
                <Input value={editKey.webhook_url || ''} onChange={e => setEditKey({...editKey, webhook_url: e.target.value})} placeholder="https://seusite.com/webhooks/nexuzap" className="h-9 mt-1" />
                <p className="text-[11px] text-muted-foreground mt-1">Usado quando o pagamento é gerado sem callback_url.</p>
              </div>
              <div>
                <Label className="text-xs mb-2 block">Permissões</Label>
                {ALL_PERMISSIONS.map(p => (
                  <label key={p.key} className="flex items-center gap-2 text-xs cursor-pointer p-1 rounded hover:bg-muted/30">
                    <Checkbox checked={editKey.permissions?.includes(p.key)} onCheckedChange={c => {
                      const perms = editKey.permissions || [];
                      setEditKey({...editKey, permissions: c ? [...perms, p.key] : perms.filter(x => x !== p.key)});
                    }} />
                    {p.label}
                  </label>
                ))}
              </div>
              <Button onClick={handleUpdate} className="w-full">Salvar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
